import React, {FC} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {buildSettingsBillingUrl} from '../../../models/url/settingsUrlHelpers';
import {getDaysLeftInTrial} from '../../../store/data/company/companySelectors';
import {useLinkNavigation} from '../../common/navigation/useLinkNavigation';

interface TrialConversationProps {
  isInSidebar: boolean;
}

const StyledCardDiv = styled.div`
  border-radius: 16px;
  background: ${(p) => p.theme.foreground};
  ${(p) => boxShadow(p.theme, `0 1px 9px ${p.theme.alphas.black20}`)};
  border: 1px solid ${(p) => p.theme.alphas.black20};
  padding: 20px;

  display: grid;
  grid-template-areas: 'text buttons';
  grid-template-columns: 1fr auto;
`;

const StyledTextContainerDiv = styled.div`
  grid-area: text;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StyledHeaderSpan = styled.span`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.veryLarge};
`;

const StyledButtonContainerDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  grid-area: buttons;
`;

const StyledChecklistCard = styled.div`
  padding: 12px;
  border-radius: 10px;

  background-color: ${(p) => p.theme.greys.shade20};
`;

const StyledCardTitle = styled.div`
  margin-right: 5px;
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.medium};
`;

const StyledCardDescription = styled.div`
  padding-top: 4px;
  padding-bottom: 10px;

  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledBillingButton = styled(ChromelessButton)`
  padding: 7px 12px;
  border: 1px solid ${(p) => p.theme.alphas.black20};
  border-radius: 100px;

  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.medium};
  text-align: center;

  &:hover {
    background-color: ${(p) => p.theme.alphas.gray10};
  }
`;

export const TrialConversionCard: FC<TrialConversationProps> = (props) => {
  const trialDaysLeft = useAppSelector(getDaysLeftInTrial);
  const {navigateToLink} = useLinkNavigation();

  const trialConversionDescription =
    trialDaysLeft > 0 ? (
      <Trans id="trial-conversion-plan-text">
        Upgrade your plan or schedule a call with us to avoid service disruption.
      </Trans>
    ) : (
      <Trans id="trial-conversion-plan-text-ended">Please upgrade your plan to keep using Front.</Trans>
    );

  const trialConversionTitle = (
    <Plural
      id="trial-conversion-days-left"
      value={trialDaysLeft}
      _0="Your free trial has ended"
      one="Your trial ends within 24 hours"
      other="Your trial ends in # days"
    />
  );

  const trialConversionButtonText = <Trans id="trial-conversion-purchase-button">Upgrade plan</Trans>;

  if (props.isInSidebar) {
    return (
      <StyledChecklistCard>
        <StyledCardTitle>{trialConversionTitle}</StyledCardTitle>
        <StyledCardDescription>{trialConversionDescription}</StyledCardDescription>
        <StyledBillingButton onClick={() => navigateToLink(buildSettingsBillingUrl())}>
          {/* TODO-ONBOARD: Add a Learn More link whenever that's ready */}
          {trialConversionButtonText}
        </StyledBillingButton>
      </StyledChecklistCard>
    );
  }

  return (
    <StyledCardDiv>
      <StyledTextContainerDiv>
        <StyledHeaderSpan>{trialConversionTitle}</StyledHeaderSpan>
        {trialConversionDescription}
      </StyledTextContainerDiv>
      <StyledButtonContainerDiv>
        {/* TODO-ONBOARD: Add a Learn More link whenever that's ready */}
        <Button
          variant={ButtonVariantsEnum.PRIMARY}
          onClick={() => navigateToLink(buildSettingsBillingUrl())}
        >
          {trialConversionButtonText}
        </Button>
      </StyledButtonContainerDiv>
    </StyledCardDiv>
  );
};
